import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Plus, Play, Square, RotateCw, Layers, Cpu, Clock, TrendingDown, Trash2,
  ChevronRight, AlertCircle, CheckCircle, ArrowRight, Zap, Network, X,
  GripVertical, Link2,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { staggerContainer, staggerItem, hoverLift, fadeInUp } from "@/config/animations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLATFORM_COLORS = {
  crewai: "#10b981",
  autogen: "#3b82f6",
  langgraph: "#f59e0b",
  manus: "#8b5cf6",
  native: "#06b6d4",
};

const STATE_COLORS = {
  draft: "var(--qn-foreground-muted)",
  optimizing: "var(--qn-primary)",
  optimized: "#10b981",
  executing: "#f59e0b",
  completed: "#10b981",
  failed: "#ef4444",
  cancelled: "#6b7280",
  rolled_back: "#f59e0b",
};

const OrchestrationDAG = ({ tasks, assignments, dataTransfers }) => {
  const assignMap = {};
  (assignments || []).forEach(a => { assignMap[a.task_id] = a; });
  const transferMap = {};
  (dataTransfers || []).forEach(dt => {
    transferMap[`${dt.from_task}-${dt.to_task}`] = dt;
  });

  const levels = [];
  const placed = new Set();
  const taskMap = {};
  tasks.forEach(t => { taskMap[t.id] = t; });

  let safety = 0;
  while (placed.size < tasks.length && safety < 50) {
    const level = tasks.filter(t => !placed.has(t.id) && t.dependencies.every(d => placed.has(d)));
    if (level.length === 0) break;
    levels.push(level);
    level.forEach(t => placed.add(t.id));
    safety++;
  }

  const nodePos = {};
  const nodeW = 130, nodeH = 52, gapX = 40, gapY = 90;
  levels.forEach((level, li) => {
    const totalW = level.length * nodeW + (level.length - 1) * gapX;
    const startX = (700 - totalW) / 2;
    level.forEach((t, ti) => {
      nodePos[t.id] = { x: startX + ti * (nodeW + gapX), y: li * (nodeH + gapY) + 20 };
    });
  });

  const svgH = levels.length * (nodeH + gapY) + 20;

  return (
    <div className="relative w-full overflow-x-auto rounded-xl border border-[var(--qn-border)] bg-[var(--qn-background)]" style={{ minHeight: Math.max(svgH, 120) + 20 }}>
      <svg width="700" height={Math.max(svgH, 120)} className="mx-auto">
        {/* Edges */}
        {tasks.map(t => t.dependencies.map(dep => {
          const from = nodePos[dep]; const to = nodePos[t.id];
          if (!from || !to) return null;
          const hasDT = transferMap[`${dep}-${t.id}`];
          return (
            <g key={`edge-${dep}-${t.id}`}>
              <motion.line
                x1={from.x + nodeW / 2} y1={from.y + nodeH}
                x2={to.x + nodeW / 2} y2={to.y}
                stroke={hasDT ? "#ef4444" : "var(--qn-border)"}
                strokeWidth={hasDT ? 2.5 : 1.5}
                strokeDasharray={hasDT ? "6 3" : "none"}
                initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.5 }}
              />
              {hasDT && (
                <text
                  x={(from.x + to.x) / 2 + nodeW / 2 + 8}
                  y={(from.y + nodeH + to.y) / 2 + 4}
                  fontSize="8" fill="#ef4444" fontWeight="500"
                >{hasDT.method}</text>
              )}
            </g>
          );
        }))}
        {/* Nodes */}
        {tasks.map((t) => {
          const pos = nodePos[t.id];
          if (!pos) return null;
          const a = assignMap[t.id];
          const platform = a?.assigned_platform || t.platform || "native";
          const color = PLATFORM_COLORS[platform] || "#6b7280";
          const capMatch = a?.capability_match;
          return (
            <g key={t.id}>
              <motion.rect x={pos.x} y={pos.y} width={nodeW} height={nodeH} rx={10}
                fill="var(--qn-card)" stroke={color} strokeWidth={2.5}
                initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
              />
              <title>{t.name}</title>
              <text x={pos.x + nodeW / 2} y={pos.y + 18} textAnchor="middle" fontSize="10" fill="var(--qn-foreground)" fontWeight="600">{(t.name || "").length > 18 ? t.name.slice(0, 16) + "..." : t.name}</text>
              <text x={pos.x + nodeW / 2} y={pos.y + 32} textAnchor="middle" fontSize="9" fill={color} fontWeight="600">{platform}</text>
              <text x={pos.x + nodeW / 2} y={pos.y + 44} textAnchor="middle" fontSize="8" fill="var(--qn-foreground-muted)">
                {a ? a.assigned_agent : (t.agent || "auto")}
              </text>
              {capMatch !== undefined && capMatch !== null && (
                <circle cx={pos.x + nodeW - 8} cy={pos.y + 8} r={5}
                  fill={capMatch >= 0.8 ? "#10b981" : capMatch >= 0.5 ? "#f59e0b" : "#ef4444"} />
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
};

// Workflow Builder Component
const WorkflowBuilder = ({ onSubmit, loading, platformData }) => {
  const [workflowName, setWorkflowName] = useState("Cross-Platform Pipeline");
  const [tasks, setTasks] = useState([]);
  const [autoExecute, setAutoExecute] = useState(false);
  const [superOptimize, setSuperOptimize] = useState(false);

  const platforms = platformData?.platforms || {};
  const taskTypes = platformData?.task_types || [];

  const addTask = () => {
    const id = `task_${Date.now()}`;
    setTasks(prev => [...prev, {
      id, name: "", platform: "crewai", agent: "gpt-4o",
      estimated_tokens: 3000, dependencies: [],
      task_type: "general", capabilities_required: [],
    }]);
  };

  const updateTask = (idx, field, value) => {
    setTasks(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const removeTask = (idx) => {
    const removedId = tasks[idx].id;
    setTasks(prev => prev
      .filter((_, i) => i !== idx)
      .map(t => ({
        ...t,
        dependencies: t.dependencies.filter(d => d !== removedId),
      }))
    );
  };

  const toggleDep = (taskIdx, depId) => {
    setTasks(prev => prev.map((t, i) => {
      if (i !== taskIdx) return t;
      const deps = t.dependencies.includes(depId)
        ? t.dependencies.filter(d => d !== depId)
        : [...t.dependencies, depId];
      return { ...t, dependencies: deps };
    }));
  };

  const handleSubmit = () => {
    if (tasks.length === 0) {
      toast.error("Add at least one task");
      return;
    }
    const namedTasks = tasks.map((t, i) => ({
      ...t,
      name: t.name || `Task ${i + 1}`,
    }));
    onSubmit({
      workflow: { name: workflowName, tasks: namedTasks },
      auto_execute: autoExecute,
      super_optimize: superOptimize,
    });
  };

  const loadPreset = (preset) => {
    if (preset === "research") {
      setWorkflowName("Multi-Platform Research Pipeline");
      setTasks([
        { id: "research", name: "Deep Research", platform: "crewai", agent: "gpt-4o", estimated_tokens: 5000, dependencies: [], task_type: "research", capabilities_required: ["reasoning", "research"] },
        { id: "analyze", name: "Data Analysis", platform: "autogen", agent: "claude-sonnet", estimated_tokens: 4000, dependencies: ["research"], task_type: "analysis", capabilities_required: ["reasoning", "analysis"] },
        { id: "write", name: "Content Writing", platform: "langgraph", agent: "gpt-4o", estimated_tokens: 8000, dependencies: ["research"], task_type: "writing", capabilities_required: ["creative"] },
        { id: "review", name: "Quality Review", platform: "manus", agent: "claude-haiku", estimated_tokens: 3000, dependencies: ["analyze", "write"], task_type: "review", capabilities_required: ["reasoning"] },
        { id: "publish", name: "Publish Output", platform: "native", agent: "gpt-4o-mini", estimated_tokens: 1000, dependencies: ["review"], task_type: "general", capabilities_required: [] },
      ]);
    } else if (preset === "code") {
      setWorkflowName("Code Review Pipeline");
      setTasks([
        { id: "spec", name: "Write Specification", platform: "crewai", agent: "gpt-5.2", estimated_tokens: 4000, dependencies: [], task_type: "writing", capabilities_required: ["reasoning", "creative"] },
        { id: "implement", name: "Code Implementation", platform: "autogen", agent: "claude-sonnet", estimated_tokens: 10000, dependencies: ["spec"], task_type: "code", capabilities_required: ["code", "reasoning"] },
        { id: "test", name: "Write Tests", platform: "langgraph", agent: "gpt-4o", estimated_tokens: 6000, dependencies: ["implement"], task_type: "code", capabilities_required: ["code"] },
        { id: "review", name: "Code Review", platform: "manus", agent: "claude-opus", estimated_tokens: 5000, dependencies: ["implement", "test"], task_type: "review", capabilities_required: ["reasoning", "code"] },
      ]);
    }
  };

  return (
    <div className="space-y-5" data-testid="workflow-builder" data-automation-id="workflow-builder">
      <div>
        <label className="text-sm font-medium mb-1.5 block text-[var(--qn-foreground-muted)]">Workflow Name</label>
        <Input value={workflowName} onChange={(e) => setWorkflowName(e.target.value)}
          className="bg-[var(--qn-background)] border-[var(--qn-border)]" data-testid="orch-name-input" data-automation-id="orch-name-input" />
      </div>

      {/* Presets */}
      <div className="flex gap-2">
        <span className="text-xs text-[var(--qn-foreground-subtle)] self-center mr-1">Presets:</span>
        <Button size="sm" variant="outline" onClick={() => loadPreset("research")} data-testid="preset-research" data-automation-id="preset-research" className="text-xs h-7">
          Research Pipeline
        </Button>
        <Button size="sm" variant="outline" onClick={() => loadPreset("code")} data-testid="preset-code" data-automation-id="preset-code" className="text-xs h-7">
          Code Review
        </Button>
      </div>

      {/* Tasks */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-[var(--qn-foreground-muted)]">Tasks ({tasks.length})</label>
          <Button size="sm" variant="outline" onClick={addTask} data-testid="add-task-btn" data-automation-id="add-task-btn" className="h-7 text-xs">
            <Plus className="w-3 h-3 mr-1" /> Add Task
          </Button>
        </div>

        <div className="max-h-[350px] overflow-y-auto space-y-2 pr-1">
          <AnimatePresence>
            {tasks.map((task, idx) => (
              <motion.div
                key={task.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                className="border border-[var(--qn-border)] rounded-lg p-3 bg-[var(--qn-background)] space-y-2"
                data-testid={`task-row-${idx}`}
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: PLATFORM_COLORS[task.platform] || "#6b7280" }} />
                  <Input
                    value={task.name} onChange={(e) => updateTask(idx, "name", e.target.value)}
                    placeholder={`Task ${idx + 1} name`}
                    className="h-7 text-sm bg-transparent border-[var(--qn-border)] flex-1"
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6 shrink-0 text-[var(--qn-foreground-subtle)] hover:text-red-400" onClick={() => removeTask(idx)}>
                    <X className="w-3 h-3" />
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <Select value={task.platform} onValueChange={(v) => updateTask(idx, "platform", v)}>
                    <SelectTrigger className="h-7 text-xs bg-transparent border-[var(--qn-border)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      {Object.entries(platforms).map(([k, p]) => (
                        <SelectItem key={k} value={k}>
                          <span className="flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color }} />
                            {p.name}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={task.agent} onValueChange={(v) => updateTask(idx, "agent", v)}>
                    <SelectTrigger className="h-7 text-xs bg-transparent border-[var(--qn-border)]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      {(platforms[task.platform]?.agents || []).map(a => (
                        <SelectItem key={a.agent_id} value={a.agent_id}>{a.name} (${(a.cost_per_token * 1000).toFixed(3)}/1k)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    type="number" value={task.estimated_tokens}
                    onChange={(e) => updateTask(idx, "estimated_tokens", parseInt(e.target.value) || 1000)}
                    className="h-7 text-xs bg-transparent border-[var(--qn-border)]" placeholder="Tokens"
                  />
                </div>

                {/* Dependencies */}
                {idx > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Link2 className="w-3 h-3 text-[var(--qn-foreground-subtle)]" />
                    <span className="text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Depends on:</span>
                    {tasks.slice(0, idx).map(prev => (
                      <button
                        key={prev.id}
                        onClick={() => toggleDep(idx, prev.id)}
                        className={`text-[10px] px-1.5 py-0.5 rounded-md border transition-all ${
                          task.dependencies.includes(prev.id)
                            ? "border-[var(--qn-primary)] bg-[var(--qn-primary-muted)] text-[var(--qn-primary)]"
                            : "border-[var(--qn-border)] text-[var(--qn-foreground-subtle)] hover:border-[var(--qn-foreground-muted)]"
                        }`}
                      >
                        {prev.name || `Task ${tasks.indexOf(prev) + 1}`}
                      </button>
                    ))}
                  </div>
                )}

                {/* Task Type */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Zap className="w-3 h-3 text-[var(--qn-foreground-subtle)]" />
                  <span className="text-[10px] text-[var(--qn-foreground-subtle)] mr-1">Type:</span>
                  <Select value={task.task_type || "general"} onValueChange={(v) => {
                    const tt = taskTypes.find(t => t.id === v);
                    updateTask(idx, "task_type", v);
                    if (tt?.capabilities) updateTask(idx, "capabilities_required", tt.capabilities);
                  }}>
                    <SelectTrigger className="h-6 text-[10px] bg-transparent border-[var(--qn-border)] w-auto min-w-[90px]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[var(--qn-card)] border-[var(--qn-border)]">
                      {taskTypes.map(tt => (
                        <SelectItem key={tt.id} value={tt.id} className="text-xs">{tt.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {tasks.length === 0 && (
          <div className="text-center py-6 text-sm text-[var(--qn-foreground-subtle)] border border-dashed border-[var(--qn-border)] rounded-lg">
            No tasks yet. Add tasks or use a preset to get started.
          </div>
        )}
      </div>

      {/* Options */}
      <div className="space-y-2 pt-1 border-t border-[var(--qn-border)]">
        <div className="flex items-center justify-between">
          <label className="text-sm">Auto-Execute After Optimization</label>
          <Switch checked={autoExecute} onCheckedChange={setAutoExecute} data-testid="orch-auto-execute-toggle" data-automation-id="orch-auto-execute-toggle" />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm">Quantum Super Optimize</label>
          <Switch checked={superOptimize} onCheckedChange={setSuperOptimize} data-testid="orch-super-optimize-toggle" data-automation-id="orch-super-optimize-toggle" />
        </div>
      </div>

      {/* Platform summary */}
      {tasks.length > 0 && (
        <div className="bg-[var(--qn-background)] rounded-lg p-3 border border-[var(--qn-border)]">
          <p className="text-xs font-medium text-[var(--qn-foreground-muted)] mb-1.5">{tasks.length} tasks across {new Set(tasks.map(t => t.platform)).size} platforms</p>
          <div className="flex gap-2 flex-wrap">
            {[...new Set(tasks.map(t => t.platform))].map(p => (
              <Badge key={p} style={{ backgroundColor: (PLATFORM_COLORS[p] || "#6b7280") + "20", color: PLATFORM_COLORS[p] || "#6b7280" }} className="text-xs capitalize">{p} ({tasks.filter(t => t.platform === p).length})</Badge>
            ))}
          </div>
        </div>
      )}

      {/* Submit */}
      <Button onClick={handleSubmit} disabled={loading || tasks.length === 0} className="w-full btn-primary" data-testid="confirm-create-orch-btn" data-automation-id="confirm-create-orch-btn">
        {loading ? (
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity }} className="w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
        ) : (
          <><Layers className="w-4 h-4 mr-2" /> Create & Optimize ({tasks.length} tasks)</>
        )}
      </Button>
    </div>
  );
};

// Data Transfers Card
const DataTransfersCard = ({ transfers }) => {
  if (!transfers || transfers.length === 0) return null;
  return (
    <Card className="card-themed">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Network className="w-4 h-4 text-red-400" /> Inter-Platform Data Transfers
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {transfers.map((dt, i) => (
          <div key={i} className="flex items-center gap-3 bg-[var(--qn-background)] rounded-lg p-2.5 text-sm">
            <Badge style={{ backgroundColor: PLATFORM_COLORS[dt.from_platform] + "20", color: PLATFORM_COLORS[dt.from_platform] }} className="text-xs">{dt.from_task}</Badge>
            <ArrowRight className="w-3 h-3 text-red-400 shrink-0" />
            <Badge style={{ backgroundColor: PLATFORM_COLORS[dt.to_platform] + "20", color: PLATFORM_COLORS[dt.to_platform] }} className="text-xs">{dt.to_task}</Badge>
            <div className="ml-auto flex items-center gap-3 text-xs text-[var(--qn-foreground-muted)]">
              <span className="font-mono">{dt.method}</span>
              <span>{dt.latency_ms}ms</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export default function OrchestrationTab() {
  const [orchestrations, setOrchestrations] = useState([]);
  const [selectedOrch, setSelectedOrch] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [platformData, setPlatformData] = useState(null);

  useEffect(() => {
    fetchOrchestrations();
    fetchPlatformData();
  }, []);

  const fetchOrchestrations = async () => {
    try {
      const res = await axios.get(`${API}/orchestrations`);
      // Deduplicate: group by name, keep best version (optimized > completed > others)
      const byName = {};
      const statePriority = { optimized: 3, completed: 2, executing: 1, draft: 0, failed: -1, cancelled: -2, rolled_back: -1 };
      (res.data || []).forEach((o) => {
        const key = o.name || o.orchestration_id;
        const existing = byName[key];
        if (!existing || (statePriority[o.state] || 0) > (statePriority[existing.state] || 0)) {
          byName[key] = o;
        }
      });
      setOrchestrations(Object.values(byName));
    } catch { /* ignore */ }
  };

  const fetchPlatformData = async () => {
    try {
      const res = await axios.get(`${API}/agents/cross-platform`);
      setPlatformData(res.data);
    } catch { /* ignore */ }
  };

  const handleCreate = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/orchestrate`, payload);
      toast.success(`Orchestration created! ${res.data.optimization?.cost_reduction_percent || 0}% cost saved`);
      setCreateDialogOpen(false);
      fetchOrchestrations();
      setSelectedOrch(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create orchestration");
    } finally {
      setLoading(false);
    }
  };

  const handleExecute = async (orchId) => {
    try {
      await axios.post(`${API}/orchestrations/${orchId}/execute`);
      toast.success("Orchestration executed!");
      fetchOrchestrations();
      const detail = await axios.get(`${API}/orchestrations/${orchId}`);
      setSelectedOrch(detail.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Execution failed");
    }
  };

  const handleCancel = async (orchId) => {
    try {
      await axios.post(`${API}/orchestrations/${orchId}/cancel`);
      toast.success("Orchestration cancelled");
      fetchOrchestrations();
    } catch (e) {
      toast.error("Failed to cancel");
    }
  };

  const loadDetail = async (orchId) => {
    try {
      const res = await axios.get(`${API}/orchestrations/${orchId}`);
      setSelectedOrch(res.data);
    } catch {
      toast.error("Failed to load orchestration");
    }
  };

  const opt = selectedOrch?.optimization;
  const plan = selectedOrch?.execution_plan || [];

  return (
    <motion.div {...fadeInUp} className="space-y-6" data-testid="orchestration-tab" data-automation-id="orchestration-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-[var(--qn-foreground)]">Cross-Platform Orchestration</h2>
          <p className="text-base text-[var(--qn-foreground-muted)]">Build & optimize workflows across CrewAI, AutoGen, LangGraph & Manus</p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary" data-testid="new-orchestration-btn" data-automation-id="new-orchestration-btn">
          <Plus className="w-4 h-4 mr-2" /> New Orchestration
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: List */}
        <div className="space-y-3">
          <h3 className="text-base font-medium text-[var(--qn-foreground-muted)]">Recent Orchestrations</h3>
          {orchestrations.length === 0 && (
            <Card className="card-themed">
              <CardContent className="p-6 text-center text-base text-[var(--qn-foreground-muted)]">
                No orchestrations yet. Create one to get started.
              </CardContent>
            </Card>
          )}
          <motion.div variants={staggerContainer} initial="initial" animate="animate" className="space-y-2">
            {orchestrations.map((o) => (
              <motion.div key={o.orchestration_id} variants={staggerItem} whileHover={hoverLift.whileHover}>
                <Card
                  className={`card-themed cursor-pointer transition-all ${selectedOrch?.orchestration_id === o.orchestration_id ? "ring-2 ring-[var(--qn-primary)]" : ""}`}
                  onClick={() => loadDetail(o.orchestration_id)}
                  data-testid={`orch-card-${o.orchestration_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-base font-medium truncate max-w-[160px]" title={o.name}>{o.name}</span>
                      <Badge style={{ backgroundColor: STATE_COLORS[o.state] + "20", color: STATE_COLORS[o.state] }} className="text-xs shrink-0 ml-2">
                        {o.state}
                      </Badge>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {(o.platforms_involved || []).map(p => (
                        <span key={p} className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: PLATFORM_COLORS[p] }} title={p} />
                      ))}
                    </div>
                    <div className="text-sm text-[var(--qn-foreground-muted)] mt-1">
                      {(o.tasks || []).length} {(o.tasks || []).length === 1 ? "task" : "tasks"} &middot; {new Date(o.created_at).toLocaleDateString()}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Right: Detail */}
        <div className="lg:col-span-2 space-y-4">
          {!selectedOrch ? (
            <Card className="card-themed">
              <CardContent className="p-12 text-center text-[var(--qn-foreground-muted)]">
                <Layers className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-base">Select an orchestration or create a new one</p>
                <p className="text-sm mt-2">Build custom workflows that span multiple AI platforms</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Title & Actions */}
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold break-words">{selectedOrch.name}</h3>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    {(selectedOrch.platforms_involved || []).map(p => (
                      <Badge key={p} style={{ backgroundColor: PLATFORM_COLORS[p] + "20", color: PLATFORM_COLORS[p] }} className="text-sm capitalize">
                        {p}
                      </Badge>
                    ))}
                    <Badge style={{ backgroundColor: STATE_COLORS[selectedOrch.state] + "20", color: STATE_COLORS[selectedOrch.state] }} className="text-sm" data-testid="orch-state-badge" data-automation-id="orch-state-badge">
                      {selectedOrch.state}
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selectedOrch.state === "optimized" && (
                    <Button size="sm" className="btn-primary" onClick={() => handleExecute(selectedOrch.orchestration_id)} data-testid="execute-orch-btn" data-automation-id="execute-orch-btn">
                      <Play className="w-3 h-3 mr-1" /> Execute
                    </Button>
                  )}
                  {["executing", "optimizing"].includes(selectedOrch.state) && (
                    <Button size="sm" variant="destructive" onClick={() => handleCancel(selectedOrch.orchestration_id)} data-testid="cancel-orch-btn" data-automation-id="cancel-orch-btn">
                      <Square className="w-3 h-3 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              </div>

              {/* Metrics */}
              {opt && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: "Cost Reduction", value: `${opt.cost_reduction_percent}%`, icon: TrendingDown, color: "#10b981" },
                    { label: "Time Reduction", value: `${opt.time_reduction_percent}%`, icon: Clock, color: "#3b82f6" },
                    { label: "Solver", value: opt.solver_type?.replace("_", " "), icon: Cpu, color: "var(--qn-primary)" },
                    { label: "Opt Time", value: `${opt.optimization_time_ms}ms`, icon: Clock, color: "#f59e0b" },
                  ].map((m, i) => (
                    <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                      className="card-themed rounded-lg p-3"
                    >
                      <div className="flex items-center gap-1 text-[var(--qn-foreground-muted)] text-sm mb-1">
                        <m.icon className="w-3.5 h-3.5" style={{ color: m.color }} /> {m.label}
                      </div>
                      <div className="text-base font-semibold" style={{ color: m.color }}>{m.value}</div>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* Transfer Cost Summary */}
              {opt && (opt.total_transfer_cost > 0 || opt.total_transfer_latency_ms > 0) && (
                <div className="flex gap-4 text-sm text-[var(--qn-foreground-muted)] bg-[var(--qn-surface)] p-3 rounded-lg">
                  <span>Data Transfers: <strong className="text-[var(--qn-foreground)]">{opt.data_transfers?.length || 0}</strong></span>
                  <span>Transfer Latency: <strong className="text-[var(--qn-foreground)]">{opt.total_transfer_latency_ms}ms</strong></span>
                </div>
              )}

              {/* Platform Breakdown */}
              {opt?.platform_breakdown && (
                <Card className="card-themed">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Platform Breakdown</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {Object.entries(opt.platform_breakdown).map(([p, data]) => (
                      <div key={p} className="flex items-center gap-3">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: data.color }} />
                        <span className="text-base font-medium w-24 capitalize">{p}</span>
                        <div className="flex-1">
                          <Progress value={(data.tasks / (selectedOrch.tasks?.length || 1)) * 100}
                            className="h-2" style={{ "--progress-color": data.color }} />
                        </div>
                        <span className="text-sm text-[var(--qn-foreground-muted)]">{data.tasks} {data.tasks === 1 ? "task" : "tasks"} &middot; ${data.cost?.toFixed(4)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* DAG Visualization */}
              <Card className="card-themed">
                <CardHeader className="pb-2"><CardTitle className="text-base">Workflow DAG</CardTitle></CardHeader>
                <CardContent>
                  <OrchestrationDAG
                    tasks={selectedOrch.tasks || []}
                    assignments={opt?.assignments}
                    dataTransfers={opt?.data_transfers}
                  />
                  {/* Legend */}
                  <div className="flex gap-4 mt-3 flex-wrap">
                    {Object.entries(PLATFORM_COLORS).map(([k, c]) => (
                      <div key={k} className="flex items-center gap-1.5 text-xs text-[var(--qn-foreground-muted)]">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />
                        <span className="capitalize">{k}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-1.5 text-xs text-red-400">
                      <span className="w-4 border-t-2 border-dashed border-red-400" />
                      <span>Data Transfer</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Data Transfers */}
              <DataTransfersCard transfers={opt?.data_transfers} />

              {/* Execution Plan */}
              {plan.length > 0 && (
                <Card className="card-themed">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Execution Plan</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {plan.map((stage, si) => (
                      <div key={si} className="border border-[var(--qn-border)] rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline" className="text-sm">Stage {stage.stage}</Badge>
                          {stage.parallel && <Badge className="text-xs bg-blue-500/20 text-blue-400">Parallel</Badge>}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {stage.tasks.map((task) => {
                            const stColor = task.state === "completed" ? "#10b981" : task.state === "failed" ? "#ef4444" : "var(--qn-foreground-muted)";
                            return (
                              <div key={task.task_id} className="flex items-center justify-between bg-[var(--qn-background)] rounded px-3 py-2 text-sm">
                                <div className="flex items-center gap-2">
                                  {task.state === "completed" ? <CheckCircle className="w-3 h-3 text-green-400" /> : task.state === "failed" ? <AlertCircle className="w-3 h-3 text-red-400" /> : <Clock className="w-3 h-3 text-gray-400" />}
                                  <span>{task.task_name}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="capitalize" style={{ color: PLATFORM_COLORS[task.platform] }}>{task.platform}</span>
                                  <span className="text-[var(--qn-foreground-muted)]">{task.agent}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[var(--qn-card)] border-[var(--qn-border)] max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Cross-Platform Orchestration</DialogTitle>
            <DialogDescription>Build a custom workflow spanning multiple AI platforms, then optimize with quantum annealing</DialogDescription>
          </DialogHeader>
          <WorkflowBuilder
            onSubmit={handleCreate}
            loading={loading}
            platformData={platformData}
          />
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
